<template>
  <div class="InsuranceAnotherCompanyAttachFile">
    <h2 class="gl_form-title small">
      {{ t('hiring.common.insurance-another-company-attach-file.form-legend') }}
      {{ documentTypeTitle }}
    </h2>
    <p v-if="!showQR">
      {{ t('hiring.common.insurance-another-company-attach-file.description-view') }}
    </p>
    <template v-else>
      <QRComponent
        :value="qrUrl"
        :label="qrLabel"
        :error="qrError"
        :isFileAttached="isFileAttached"
        @retry="checkQRDocument"
      >
        <p>
          {{ t('hiring.common.insurance-another-company-attach-file.attach-file-hint') }}
        </p>
      </QRComponent>
    </template>
    <BaseFile
      :key="fileKeyRefreshed"
      id="document"
      v-model="documentFiles"
      :rules="[validationRules.requiredFile]"
      :footer-message="t('hiring.common.insurance-another-company-attach-file.message-file')"
      :disabled="isFileAttached"
      @change="onChange"
      @remove="removeQR"
    />
  </div>
</template>
<script lang="ts" setup>
  import { ref, computed, onBeforeMount, watch, onBeforeUnmount } from 'vue'
  import { useI18n } from 'vue-i18n'

  import { useIsMobile } from '@/modules/core/composables/useIsMobile'
  import { useCommonStore } from '@/modules/core/stores/commonStore'
  import { useLoadingStore } from '@/modules/core/stores/loadingStore'
  import { useGTMStore } from '@/modules/core/stores/gtmStore'
  import { useRules } from '@/modules/core/composables/useRulesComposable'
  import BaseFile from '@/modules/core/components/ui/BaseFile.vue'
  import QRComponent from '@/modules/core/components/ui/QRComponent.vue'
  import SpanishBranches from '@/modules/core/models/SpanishBranches'
  import Insured from '@/modules/common/common/models/APP/Insured'
  import GTMEventType from '@/modules/core/models/GTMEventType'

  const { isMobile } = useIsMobile()

  defineExpose({ triggerSubmit })
  const emit = defineEmits(['update:validateForm'])
  const commonStore = useCommonStore()
  const loadingStore = useLoadingStore()

  interface PropsInterface {
    nextForm: String[]
    answers: Record<string, any>
    configurations: Record<string, any>
    type?: string
    extra?: Record<string, any>
  }
  const props = withDefaults(defineProps<PropsInterface>(), {})

  const { t } = useI18n()
  const { requiredFile } = useRules()

  const validationRules = {
    requiredFile
  }

  const currentInsured = new Insured(
    props.extra?.customInsuredProp
      ? props.answers[props.extra.customInsuredProp]
      : props.answers.currentInsured
  )
  const currentInsuredDocument = currentInsured.documentDetails
  const file = currentInsuredDocument.file
  const documentFiles = ref<File[]>(file ? [file] : [])
  const fileKeyRefreshed = ref(0)

  const isFileAttached = computed(() => documentFiles.value.length > 0)
  const showQR = computed(() => {
    return props.extra?.disabledQR ? false : !isMobile.value
  })
  const documentTypeTitle = computed(() => {
    if (currentInsuredDocument.documentType === 'NIF') {
      return 'DNI'
    }

    if (currentInsuredDocument.documentType === 'PAS') {
      return 'Pasaporte'
    }

    return currentInsuredDocument.documentTypeText
  })

  const gtmStore = useGTMStore()

  const qrUrl = ref(generateURl())
  const qrError = ref(false)
  const isCheckingQR = ref(false)
  const qrLabel = computed(() =>
    t('hiring.common.insurance-another-company-attach-file.qr-label', {
      document: documentTypeTitle.value
    })
  )

  watch(documentFiles, () => {
    fileKeyRefreshed.value += 1
  })

  function triggerSubmit() {
    const page = props.nextForm[0]

    const file = documentFiles.value[0]

    currentInsured.documentDetails.file = file
    currentInsured.documentDetails.fileName = file?.name?.replace(/\s/g, '') ?? 'document'

    const answers = props.extra?.customInsuredProp
      ? { [props.extra.customInsuredProp]: currentInsured }
      : props.extra?.currentInsuredIndex === 'holder'
      ? { holder: currentInsured }
      : currentInsured

    return { page, answers }
  }

  function onChange() {
    launchGTM()
    emit('update:validateForm')
  }

  function launchGTM() {
    gtmStore.sendEvent({
      event: GTMEventType.CARGA_ARCHIVO,
      tipo_incidencia: undefined,
      tipo_ramo: props.type
        ? SpanishBranches[props.type as keyof typeof SpanishBranches]
        : undefined,
      motivo_baja: undefined
    })
  }
  async function loadSavedFile() {
    const documentId = currentInsuredDocument?.documentId
    if (currentInsuredDocument.file || !documentId) return

    const file = await commonStore.fetchDocument(
      documentId,
      currentInsuredDocument.fileName!,
      currentInsuredDocument.mimeType!
    )
    if (!file) return

    documentFiles.value = [file]
    fileKeyRefreshed.value = 1
  }

  function generateURl() {
    // 'http://localhost:5173/tarificador/documento/W29iamVjdCBPYmplY3Rd'
    const hash = btoa(
      `${props.answers.quotationId}|| null ||${
        props.extra?.useCurrentInsured ? currentInsured.riskNumber : 0
      }||DocumentImage`
    )
    const url = `${window.origin}/tarificador/documento/${hash}`
    console.log('QR URL:', url)
    return url
  }

  async function checkQRDocument() {
    try {
      if (isCheckingQR.value) return
      isCheckingQR.value = true
      if (documentFiles.value.length > 0) return
      const result = await commonStore.getQRName(
        props.answers.quotationId,
        props.extra?.useCurrentInsured ? currentInsured.riskNumber : 0
      )
      if (result) {
        const qrFile = await commonStore.getQRFile(
          props.answers.quotationId,
          props.extra?.useCurrentInsured ? currentInsured.riskNumber : 0
        )
        if (!qrFile) return
        documentFiles.value = [qrFile]
        return
      }
      setTimeout(async () => await checkQRDocument(), 1000)
    } catch (error) {
      qrError.value = true
    } finally {
      isCheckingQR.value = false
    }
  }

  async function beforeMount() {
    loadingStore.startLoading()
    await commonStore.deleteQR(props.answers.quotationId)
    await loadSavedFile()
    loadingStore.stopLoading()
    if (documentFiles.value.length === 0 && showQR.value) {
      checkQRDocument()
    }
  }

  async function removeQR() {
    await commonStore.deleteQR(props.answers.quotationId)
    checkQRDocument()
  }

  onBeforeMount(beforeMount)
  onBeforeUnmount(() => (isCheckingQR.value = true))
</script>

<style lang="scss" scoped>
  .InsuranceAnotherCompanyAttachFile {
    .description-view {
      margin: $spacing-xl 0;
      color: $color-neutral;
      font-size: $font-size-m;
      font-weight: $font-weight-400;
    }

    p {
      color: $color-neutral;
    }
  }
</style>
